import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import SectorUsageLineChart from './SectorUsageLineChart';
import { isSectorUsageSection } from './sectorUsageChartUtils';
import { formatAppliedFilterChip } from './filterLabelUtils';

const CATEGORICAL_HINTS = [
  'status', 'type', 'category', 'state', 'priority', 'active',
  'entitytype', 'department', 'role', 'condition', 'severity',
  'classification', 'source', 'aircraft'
];
const SKIP_HINTS = [
  'id', 'serial', 'description', 'address', 'email', 'notes',
  'globalid', 'displayid', 'partnumber', 'registration'
];
const DATE_HINTS = ['date', 'due', 'created', 'updated', 'scheduled', 'completed', 'entry'];

const PALETTE = [
  '#1976d2', '#ed6c02', '#9c27b0', '#0288d1', '#f57c00',
  '#7b1fa2', '#00838f', '#ef6c00', '#5e35b1', '#1565c0',
  '#e65100', '#6a1b9a', '#455a64', '#5c6bc0', '#ff8f00',
  '#3949ab', '#00acc1', '#8e24aa', '#546e7a', '#ffb300'
];

const STATUS_COLORS = {
  active: '#1976d2',
  operational: '#0288d1',
  complete: '#5c6bc0',
  completed: '#5c6bc0',
  closed: '#546e7a',
  approved: '#1565c0',
  open: '#0288d1',
  pending: '#ed6c02',
  inprogress: '#f57c00',
  scheduled: '#7b1fa2',
  overdue: '#e65100',
  rejected: '#6a1b9a',
  cancelled: '#757575',
  canceled: '#757575',
  inactive: '#9e9e9e',
  archived: '#78909c',
  critical: '#ff8f00',
  high: '#ef6c00',
  medium: '#f9a825',
  low: '#00acc1',
  warning: '#ef6c00',
  error: '#8e24aa',
  success: '#1976d2'
};

const SUMMARY_CHIP_COLORS = [
  { bg: '#e3f2fd', color: '#1565c0' },
  { bg: '#e0f7fa', color: '#00838f' },
  { bg: '#fff3e0', color: '#e65100' },
  { bg: '#f3e5f5', color: '#7b1fa2' }
];

function normalizeKey(key) {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function shouldSkipColumn(key) {
  const normalized = normalizeKey(key);
  return SKIP_HINTS.some((hint) => normalized.includes(hint));
}

function isCategoricalHint(key) {
  const normalized = normalizeKey(key);
  return CATEGORICAL_HINTS.some((hint) => normalized.includes(hint));
}

function isDateHint(key) {
  const normalized = normalizeKey(key);
  return DATE_HINTS.some((hint) => normalized.includes(hint));
}

function isDateLike(value) {
  if (!value) return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str);
    return !Number.isNaN(d.getTime());
  }
  return false;
}

function colorForLabel(label, index, colors) {
  const normalized = normalizeKey(label);
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    if (normalized === key || normalized.includes(key)) {
      return color;
    }
  }
  return colors[index % colors.length];
}

function withColors(data, colors) {
  return data.map((entry, index) => ({
    ...entry,
    fill: colorForLabel(entry.name, index, colors)
  }));
}

function classifyColumn(rows, key) {
  const values = rows
    .map((row) => row[key])
    .filter((value) => value !== '' && value !== null && value !== undefined);
  if (!values.length) return 'skip';

  const numericCount = values.filter((value) => {
    const num = Number(value);
    return !Number.isNaN(num) && String(value).trim() !== '';
  }).length;
  if (numericCount / values.length > 0.75) return 'numeric';

  const dateCount = values.filter(isDateLike).length;
  if (dateCount / values.length > 0.6 || isDateHint(key)) return 'date';

  const unique = new Set(values.map((value) => String(value)));
  if (unique.size > 25) return 'skip';

  return 'categorical';
}

function groupByCount(rows, key) {
  const counts = {};
  rows.forEach((row) => {
    const raw = row[key];
    const label = raw === '' || raw == null ? '(empty)' : String(raw);
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function groupByMonth(rows, key) {
  const counts = {};
  rows.forEach((row) => {
    const date = new Date(row[key]);
    if (Number.isNaN(date.getTime())) return;
    const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function topNumericByLabel(rows, labelKey, valueKey, limit = 12) {
  return rows
    .map((row) => ({
      name: String(row[labelKey] || '(empty)').slice(0, 28),
      value: Number(row[valueKey]) || 0
    }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

function pickLabelColumn(columns, rows) {
  const candidates = columns
    .filter((col) => !shouldSkipColumn(col.key))
    .map((col) => ({ col, type: classifyColumn(rows, col.key) }))
    .filter(({ type }) => type === 'categorical');

  if (!candidates.length) {
    return columns.find((col) => !shouldSkipColumn(col.key))?.key || columns[0]?.key;
  }

  candidates.sort((a, b) => {
    const aHint = isCategoricalHint(a.col.key) ? 1 : 0;
    const bHint = isCategoricalHint(b.col.key) ? 1 : 0;
    if (aHint !== bHint) return bHint - aHint;
    const aUnique = new Set(rows.map((row) => String(row[a.col.key] ?? ''))).size;
    const bUnique = new Set(rows.map((row) => String(row[b.col.key] ?? ''))).size;
    return aUnique - bUnique;
  });

  return candidates[0].col.key;
}

function chartDescription(chart) {
  if (chart.description) return chart.description;
  const { chartKind, columnLabel, labelColumnLabel, valueLabel, type } = chart;
  if (chartKind === 'categorical') {
    if (type === 'pie') {
      return `Share of rows by each ${columnLabel} value.`;
    }
    return `Row count for each ${columnLabel} value.`;
  }
  if (chartKind === 'date') {
    return `How many rows fall in each month based on ${columnLabel}.`;
  }
  if (chartKind === 'numeric') {
    return `Top values of ${valueLabel} ranked by ${labelColumnLabel}.`;
  }
  return '';
}

function buildSectionCharts(section) {
  const { rows, columns } = section;
  if (!rows?.length || !columns?.length) return [];

  const charts = [];
  const columnMeta = columns
    .filter((col) => !shouldSkipColumn(col.key))
    .map((col) => ({
      ...col,
      type: classifyColumn(rows, col.key)
    }));

  const categoricalCols = columnMeta
    .filter((col) => col.type === 'categorical')
    .sort((a, b) => {
      const aHint = isCategoricalHint(a.key) ? 1 : 0;
      const bHint = isCategoricalHint(b.key) ? 1 : 0;
      if (aHint !== bHint) return bHint - aHint;
      const aUnique = new Set(rows.map((row) => String(row[a.key] ?? ''))).size;
      const bUnique = new Set(rows.map((row) => String(row[b.key] ?? ''))).size;
      return aUnique - bUnique;
    });

  const numericCols = columnMeta.filter((col) => col.type === 'numeric');
  const dateCols = columnMeta
    .filter((col) => col.type === 'date')
    .sort((a, b) => (isDateHint(b.key) ? 1 : 0) - (isDateHint(a.key) ? 1 : 0));

  if (categoricalCols.length) {
    const col = categoricalCols[0];
    const data = groupByCount(rows, col.key);
    if (data.length) {
      charts.push({
        id: `cat-${col.key}`,
        title: `Breakdown by ${col.label}`,
        description: data.length <= 10
          ? `Share of rows by each ${col.label} value.`
          : `Row count for each ${col.label} value.`,
        chartKind: 'categorical',
        columnLabel: col.label,
        type: data.length <= 10 ? 'pie' : 'bar',
        data,
        valueLabel: 'Rows',
        colorMode: 'semantic'
      });
    }
  }

  if (dateCols.length) {
    const col = dateCols[0];
    const data = groupByMonth(rows, col.key);
    if (data.length > 1) {
      charts.push({
        id: `date-${col.key}`,
        title: `${col.label} over time`,
        description: `How many rows fall in each month based on ${col.label}.`,
        chartKind: 'date',
        columnLabel: col.label,
        type: 'bar',
        data,
        valueLabel: 'Rows',
        colorMode: 'gradient'
      });
    }
  }

  if (numericCols.length) {
    const valueCol = numericCols.find((col) => normalizeKey(col.key).includes('hour'))
      || numericCols.find((col) => !normalizeKey(col.key).includes('count'))
      || numericCols[0];
    const labelKey = pickLabelColumn(columns, rows);
    const data = topNumericByLabel(rows, labelKey, valueCol.key);
    if (data.length) {
      const labelCol = columns.find((col) => col.key === labelKey);
      charts.push({
        id: `num-${valueCol.key}`,
        title: `Top ${valueCol.label} by ${labelCol?.label || labelKey}`,
        description: `Top values of ${valueCol.label} ranked by ${labelCol?.label || labelKey}.`,
        chartKind: 'numeric',
        columnLabel: valueCol.label,
        labelColumnLabel: labelCol?.label || labelKey,
        type: 'bar',
        data,
        valueLabel: valueCol.label,
        colorMode: 'spectrum'
      });
    }
  }

  if (!charts.length && categoricalCols.length > 1) {
    const col = categoricalCols[1];
    const data = groupByCount(rows, col.key);
    if (data.length) {
      charts.push({
        id: `cat2-${col.key}`,
        title: `Breakdown by ${col.label}`,
        description: `Row count for each ${col.label} value.`,
        chartKind: 'categorical',
        columnLabel: col.label,
        type: 'bar',
        data,
        valueLabel: 'Rows',
        colorMode: 'semantic'
      });
    }
  }

  return charts;
}

function ChartTooltip({ active, payload, label, valueLabel }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const swatchColor = entry.payload?.fill || entry.color || entry.fill;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 140 }} elevation={4}>
      <Stack direction="row" spacing={1} alignItems="center">
        {swatchColor && (
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: swatchColor,
              flexShrink: 0,
              boxShadow: 1
            }}
          />
        )}
        <Box>
          <Typography variant="caption" display="block" color="text.secondary">
            {label || entry.name}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {valueLabel}: {entry.value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function formatChartValue(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return String(value ?? '');
  if (Number.isInteger(num)) return String(num);
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function renderLegendText(value, entry) {
  const count = entry?.payload?.value;
  return (
    <span style={{ color: '#424242', fontSize: 12 }}>
      {value}
      {count != null ? ` — ${formatChartValue(count)}` : ''}
    </span>
  );
}

function renderPieLabel({ name, value, percent }) {
  if (percent < 0.04) return '';
  return `${name}: ${formatChartValue(value)} (${(percent * 100).toFixed(0)}%)`;
}

function renderBarLabel(props) {
  const { x, y, width, value } = props;
  if (value == null || x == null || y == null || width == null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill="#616161"
      fontSize={11}
      textAnchor="middle"
    >
      {formatChartValue(value)}
    </text>
  );
}

function SectionChart({ chart, colors, chartIndex }) {
  const coloredData = useMemo(() => {
    if (chart.colorMode === 'gradient') {
      return chart.data.map((entry, index) => ({
        ...entry,
        fill: colors[(chartIndex + index) % colors.length]
      }));
    }
    return withColors(chart.data, colors);
  }, [chart, colors, chartIndex]);

  const { type, valueLabel } = chart;
  const description = chartDescription(chart);
  const xAxisLabel = chart.columnLabel || 'Category';
  const yAxisLabel = valueLabel || 'Value';
  const totalValue = useMemo(
    () => coloredData.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0),
    [coloredData]
  );

  const chartHeader = (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
        {chart.title}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
          {description}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        {coloredData.length} item{coloredData.length !== 1 ? 's' : ''} · {formatChartValue(totalValue)} total {yAxisLabel.toLowerCase()}
      </Typography>
    </Box>
  );

  if (type === 'pie') {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          height: '100%'
        }}
      >
        {chartHeader}
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={coloredData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="46%"
              innerRadius={52}
              outerRadius={96}
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
              label={renderPieLabel}
              labelLine={{ stroke: '#9e9e9e', strokeWidth: 1 }}
            >
              {coloredData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip valueLabel={valueLabel} />} />
            <Legend formatter={renderLegendText} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%'
      }}
    >
      {chartHeader}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={coloredData}
          margin={{ top: 24, right: 12, left: 4, bottom: coloredData.length > 6 ? 72 : 52 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#616161' }}
            interval={0}
            angle={coloredData.length > 6 ? -35 : 0}
            textAnchor={coloredData.length > 6 ? 'end' : 'middle'}
            height={coloredData.length > 6 ? 70 : 40}
            axisLine={{ stroke: '#bdbdbd' }}
            tickLine={{ stroke: '#bdbdbd' }}
            label={{
              value: xAxisLabel,
              position: 'insideBottom',
              offset: coloredData.length > 6 ? -8 : -4,
              style: { fontSize: 11, fill: '#757575', fontWeight: 600 }
            }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: '#616161' }}
            axisLine={{ stroke: '#bdbdbd' }}
            tickLine={{ stroke: '#bdbdbd' }}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              offset: 12,
              style: { fontSize: 11, fill: '#757575', fontWeight: 600, textAnchor: 'middle' }
            }}
          />
          <Tooltip content={<ChartTooltip valueLabel={valueLabel} />} cursor={{ fill: 'rgba(25, 118, 210, 0.08)' }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
            {coloredData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
            <LabelList dataKey="value" content={renderBarLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function SectionVisualization({ section, colors, sectionIndex }) {
  const charts = useMemo(() => buildSectionCharts(section), [section]);
  const accentColor = colors[sectionIndex % colors.length];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: '4px solid',
        borderLeftColor: accentColor,
        bgcolor: 'grey.50'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} mb={1}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: accentColor
              }}
            />
            <Typography variant="h6">{section.title || section.label}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>
            {section.rowCount} row{section.rowCount !== 1 ? 's' : ''}
            {section.truncated ? ' (showing first 500)' : ''}
          </Typography>
        </Box>
        {section.appliedFilters?.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            {section.appliedFilters.slice(0, 4).map((filter, index) => (
              <Chip
                key={`${section.instanceId || section.sectionKey}-${filter.key}`}
                label={formatAppliedFilterChip(filter)}
                size="small"
                sx={{
                  bgcolor: `${colors[index % colors.length]}18`,
                  color: colors[index % colors.length],
                  borderColor: `${colors[index % colors.length]}55`,
                  fontWeight: 500
                }}
                variant="outlined"
              />
            ))}
          </Stack>
        )}
      </Stack>

      {!section.rows?.length ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No data rows to visualize for this section.
        </Alert>
      ) : (
        <Box mt={2}>
          {isSectorUsageSection(section.sectionKey) && (
            <SectorUsageLineChart
              section={section}
              accentColor={accentColor}
              height={340}
            />
          )}
          {!charts.length ? (
            !isSectorUsageSection(section.sectionKey) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This section&apos;s columns are mostly identifiers or high-cardinality text — use Preview for the full table.
              </Alert>
            )
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: charts.length > 1 ? '1fr 1fr' : '1fr' }}
              gap={2}
            >
              {charts.map((chart, chartIndex) => (
                <Box key={chart.id}>
                  <SectionChart chart={chart} colors={colors} chartIndex={sectionIndex + chartIndex} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

function ReportVisualization({ data }) {
  const theme = useTheme();
  const colors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.info.main,
      ...PALETTE
    ],
    [theme]
  );

  const totalRows = useMemo(
    () => (data?.sections || []).reduce((sum, section) => sum + (section.rowCount || 0), 0),
    [data]
  );

  if (!data) return null;

  const summaryChips = [
    { label: `${data.sections?.length || 0} data section${data.sections?.length !== 1 ? 's' : ''}` },
    { label: `${totalRows} total rows` },
    ...(data.scopedAircraft ? [{ label: `Aircraft: ${data.scopedAircraft}` }] : []),
    ...(data.operatorName ? [{ label: data.operatorName }] : [])
  ];

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${colors[0]}14 0%, ${colors[2]}14 50%, ${colors[4]}14 100%)`,
          borderColor: `${colors[0]}33`
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={700}>
          {data.reportName}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {summaryChips.map((chip, index) => {
            const chipStyle = SUMMARY_CHIP_COLORS[index % SUMMARY_CHIP_COLORS.length];
            return (
              <Chip
                key={chip.label}
                label={chip.label}
                size="small"
                sx={{
                  bgcolor: chipStyle.bg,
                  color: chipStyle.color,
                  fontWeight: 600,
                  border: '1px solid',
                  borderColor: `${chipStyle.color}33`
                }}
              />
            );
          })}
        </Stack>
      </Paper>

      {!data.sections?.filter((s) => s.sectionType !== 'static').length ? (
        <Alert severity="info">
          This report has no data-table sections to visualize. Add table sections (Aircraft, Maintenance, Store, etc.) to see charts here.
        </Alert>
      ) : (
        data.sections
          .filter((section) => section.sectionType !== 'static')
          .map((section, index) => (
          <Box key={section.instanceId || section.sectionKey}>
            {index > 0 && <Divider sx={{ mb: 3 }} />}
            <SectionVisualization section={section} colors={colors} sectionIndex={index} />
          </Box>
        ))
      )}
    </Stack>
  );
}

export default ReportVisualization;
